import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Linkedin, Award, ShieldCheck } from "lucide-react";

export default function ProfileSettings() {
  const { user, refresh } = useAuth();
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [bio, setBio] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user && user !== false) {
      setName(user.name || "");
      setAvatarUrl(user.avatar_url || "");
      setLinkedinUrl(user.linkedin_url || "");
      setBio(user.bio || "");
    }
  }, [user]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/users/me", { name, avatar_url: avatarUrl, linkedin_url: linkedinUrl, bio });
      await refresh();
      toast.success("Profile updated");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const initials = (name || "?").split(" ").map(s => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

  return (
    <div className="max-w-2xl mx-auto px-6 py-14">
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#525252] mb-3">Profile</div>
      <h1 className="font-serif text-4xl font-black text-[#0A0A0A] tracking-tight mb-8">Your public identity.</h1>

      <div className="flex items-center gap-4 mb-10 p-5 border border-[#E5E5E0] bg-white">
        <Avatar className="w-16 h-16 border border-[#E5E5E0]">
          {avatarUrl ? <AvatarImage src={avatarUrl} alt={name} /> : null}
          <AvatarFallback className="bg-[#F5F5F0] text-[#0A0A0A] font-medium">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <div className="font-serif text-xl font-semibold text-[#0A0A0A]">{name || "Unnamed"}</div>
            {user?.is_verified_owner && (
              <span data-testid="profile-verified-owner" title="Verified property owner" className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-[#0A0A0A] text-white font-mono text-[9px] uppercase tracking-wider">
                <ShieldCheck className="w-2.5 h-2.5" /> Owner
              </span>
            )}
          </div>
          <div className="text-xs text-[#525252]">{user?.email}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] font-mono uppercase tracking-wider text-[#525252]">Reputation</div>
          <div data-testid="profile-reputation" className="font-serif text-3xl font-black text-[#0A0A0A] flex items-center gap-1"><Award className="w-5 h-5 text-[#C5A059]" /> {user?.reputation ?? 0}</div>
        </div>
      </div>

      <div className="space-y-5" data-testid="profile-form">
        <div>
          <label className="text-xs uppercase tracking-wider text-[#525252] font-medium">Display name</label>
          <input
            data-testid="profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1.5 w-full px-3 py-2.5 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#525252] font-medium">Avatar URL</label>
          <input
            data-testid="profile-avatar-url"
            value={avatarUrl}
            onChange={(e) => setAvatarUrl(e.target.value)}
            placeholder="https://... (LinkedIn or Gravatar photo URL)"
            className="mt-1.5 w-full px-3 py-2.5 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none"
          />
          <p className="text-[10px] text-[#525252] mt-1">Paste a public photo URL. Falls back to initials if empty.</p>
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#525252] font-medium flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" /> LinkedIn URL</label>
          <input
            data-testid="profile-linkedin"
            value={linkedinUrl}
            onChange={(e) => setLinkedinUrl(e.target.value)}
            placeholder="https://linkedin.com/in/..."
            className="mt-1.5 w-full px-3 py-2.5 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none"
          />
        </div>

        <div>
          <label className="text-xs uppercase tracking-wider text-[#525252] font-medium">Short bio</label>
          <textarea
            data-testid="profile-bio"
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            maxLength={500}
            rows={3}
            placeholder="e.g. Bought 2 flats in Bengaluru 2019-2022. Real estate agent in Powai since 2018."
            className="mt-1.5 w-full px-3 py-2.5 border border-[#E5E5E0] focus:border-[#0A0A0A] focus:outline-none resize-y"
          />
          <div className="text-[10px] font-mono text-[#525252] mt-1">{bio.length}/500 · shown on your contributions</div>
        </div>

        <div className="flex justify-end">
          <button
            data-testid="profile-save"
            onClick={save}
            disabled={saving}
            className="px-6 py-2.5 bg-[#0A0A0A] text-white hover:bg-black disabled:opacity-50 transition-colors text-sm font-medium"
          >
            {saving ? "Saving..." : "Save profile"}
          </button>
        </div>
      </div>
    </div>
  );
}
